import React, { useState, useEffect } from 'react';
import { Database, Table, Key, AlertTriangle, CheckCircle, RefreshCw, Clock, GitBranch } from 'lucide-react';
import { getAuthHeaders } from '../utils/getAuthHeaders';
import { config } from '../config';

import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Alert, AlertDescription, AlertTitle } from "./ui/alert";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

const API_URL = config.apiUrl;

// Helper function to convert SchemaSummary to expected tables format
const convertSchemaSummaryToTables = (schemaSummary) => {
  if (!schemaSummary || typeof schemaSummary !== 'object') {
    return [];
  }

  return Object.keys(schemaSummary).map(tableName => {
    const tableColumns = schemaSummary[tableName];
    
    // Handle case where columns is an array of strings (column names only)
    if (Array.isArray(tableColumns) && tableColumns.length > 0 && typeof tableColumns[0] === 'string') {
      return {
        table_name: tableName,
        columns: tableColumns.map(columnName => ({
          column_name: columnName,
          data_type: 'unknown', // Show unknown instead of assuming varchar
          is_nullable: true, // Default to nullable
          is_primary_key: columnName.toLowerCase() === 'id', // Assume 'id' is primary key
          is_foreign_key: false,
          is_unique: columnName.toLowerCase() === 'id',
          column_default: null
        }))
      };
    }
    
    // Handle case where columns is an array of objects
    return {
      table_name: tableName,
      columns: tableColumns.map(column => ({
        column_name: column.name || column.column_name || 'unknown',
        data_type: column.type || column.data_type || 'varchar',
        is_nullable: column.nullable !== false, // Default to true if not specified
        is_primary_key: column.primary_key || false,
        is_foreign_key: column.foreign_key || false,
        is_unique: column.unique || false,
        column_default: column.default || column.column_default || null
      }))
    };
  });
};

const SchemaViewer = ({ tenantId, datasourceId, agentId, dataSource, getAccessTokenSilently, className = "" }) => {
  const [currentSchema, setCurrentSchema] = useState(null);
  const [schemaVersions, setSchemaVersions] = useState([]);
  const [isLoadingSchema, setIsLoadingSchema] = useState(true);
  const [isLoadingVersions, setIsLoadingVersions] = useState(false);
  const [error, setError] = useState(null);
  const [expandedTables, setExpandedTables] = useState({});
  const [showChangeDialog, setShowChangeDialog] = useState(false);
  const [schemaChanges, setSchemaChanges] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState(null);

  useEffect(() => {
    if (dataSource && dataSource.SchemaSummary) {
      // Use existing schema data from dataSource
      console.log('Using existing schema data:', dataSource.SchemaSummary);
      setCurrentSchema({
        datasource_id: datasourceId,
        schema: { tables: convertSchemaSummaryToTables(dataSource.SchemaSummary) },
        last_updated: new Date().toISOString()
      });
      setIsLoadingSchema(false);
      loadSchemaVersions();
    } else if (datasourceId) {
      loadCurrentSchema();
      loadSchemaVersions();
    }
  }, [datasourceId, dataSource]);

  const loadCurrentSchema = async () => {
    try {
      setIsLoadingSchema(true);
      setError(null);
      
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_URL}/tenants/${tenantId}/datasources/${encodeURIComponent(datasourceId)}/schema/current`,
        {
          headers: getAuthHeaders(token)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setCurrentSchema(data);
    } catch (err) {
      console.error('Error loading schema:', err);
      setError(`Failed to load schema: ${err.message}`);
    } finally {
      setIsLoadingSchema(false);
    }
  };

  const loadSchemaVersions = async () => {
    try {
      setIsLoadingVersions(true);
      
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_URL}/tenants/${tenantId}/datasources/${encodeURIComponent(datasourceId)}/schema/versions`,
        {
          headers: getAuthHeaders(token)
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setSchemaVersions(data.versions || []);
    } catch (err) {
      console.error('Error loading schema versions:', err);
      // Don't set error here as versions are optional
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const refreshSchema = async () => {
    try {
      setIsLoadingSchema(true);
      setError(null);
      
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_URL}/tenants/${tenantId}/agents/${agentId}/discover-schema`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(token),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            datasource_id: datasourceId
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const data = await response.json();
      
      // Production response doesn't include schema immediately
      // The agent will publish the schema via IoT, so we need to poll for updates
      if (data.status === "discovery_requested") {
        setError(null);
        
        // Show a message that discovery is in progress
        const progressMessage = `Schema discovery requested (ID: ${data.request_id}). Refreshing in 3 seconds...`;
        console.log(progressMessage);
        
        // Automatically refresh after a delay to get the updated schema
        setTimeout(async () => {
          try {
            await loadCurrentSchema();
          } catch (err) {
            console.error('Failed to load updated schema:', err);
            setError('Schema discovery completed, but failed to load updated schema. Please refresh manually.');
          }
        }, 3000);
        
        setError(`${data.message} Refreshing automatically in 3 seconds...`);
      }
    } catch (err) {
      console.error('Error refreshing schema:', err);
      setError(`Failed to refresh schema: ${err.message}`);
    } finally {
      setIsLoadingSchema(false);
    }
  };

  const compareWithVersion = async (version) => {
    try {
      setError(null);
      const token = await getAccessTokenSilently();
      
      const response = await fetch(
        `${API_URL}/tenants/${tenantId}/datasources/${encodeURIComponent(datasourceId)}/schema/compare`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(token),
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            old_schema: version.schema_data,
            new_schema: currentSchema.schema
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP ${response.status}`);
      }

      const data = await response.json();
      setSchemaChanges(data);
      setSelectedVersion(version);
      setShowChangeDialog(true);
    } catch (err) {
      console.error('Error comparing schemas:', err);
      setError(`Failed to compare schemas: ${err.message}`);
    }
  };

  const toggleTable = (tableName) => {
    setExpandedTables(prev => ({
      ...prev,
      [tableName]: !prev[tableName]
    }));
  };

  const getDataTypeColor = (dataType) => {
    const type = dataType?.toLowerCase();
    if (type?.includes('int') || type?.includes('number')) return 'text-blue-600';
    if (type?.includes('varchar') || type?.includes('text') || type?.includes('string')) return 'text-green-600';
    if (type?.includes('date') || type?.includes('time')) return 'text-purple-600';
    if (type?.includes('bool')) return 'text-orange-600';
    if (type?.includes('json') || type?.includes('array')) return 'text-pink-600';
    return 'text-gray-600';
  };

  const renderColumnIcon = (column) => {
    if (column.is_primary_key) {
      return <Key className="h-3 w-3 text-yellow-500" title="Primary Key" />;
    }
    if (column.is_foreign_key) {
      return <GitBranch className="h-3 w-3 text-blue-500" title="Foreign Key" />;
    }
    return null;
  };

  const renderChangeIcon = (changeType) => {
    switch (changeType) {
      case 'table_added':
      case 'column_added':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'table_removed':
      case 'column_removed':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'column_type_changed':
      case 'table_modified':
        return <RefreshCw className="h-4 w-4 text-yellow-500" />;
      default:
        return null;
    }
  };

  if (isLoadingSchema) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Database Schema
          </CardTitle>
          <CardDescription>Loading schema information...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center p-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error && !currentSchema) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Database Schema
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <Button onClick={loadCurrentSchema} variant="outline" className="mt-4">
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const schema = currentSchema?.schema;
  const tables = schema?.tables || [];

  return (
    <>
      <Card className={className}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Database Schema
              </CardTitle>
              <CardDescription>
                {tables.length} tables • Last updated: {currentSchema?.last_updated ? 
                  new Date(currentSchema.last_updated).toLocaleString() : 'Unknown'}
              </CardDescription>
            </div>
            {agentId && (
              <Button 
                onClick={refreshSchema}
                disabled={isLoadingSchema}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <RefreshCw className={`h-4 w-4 ${isLoadingSchema ? 'animate-spin' : ''}`} />
                Refresh Schema
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="schema" className="w-full">
            <TabsList>
              <TabsTrigger value="schema">Current Schema</TabsTrigger>
              <TabsTrigger value="versions">
                Version History 
                {schemaVersions.length > 0 && (
                  <Badge variant="secondary" className="ml-2">{schemaVersions.length}</Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="schema" className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {tables.length === 0 ? (
                <Alert>
                  <Database className="h-4 w-4" />
                  <AlertTitle>No Schema Available</AlertTitle>
                  <AlertDescription>
                    The database agent has not published any schema yet. Make sure the agent is running and connected to your database.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-2">
                  {tables.map((table) => (
                    <Collapsible key={table.table_name}>
                      <CollapsibleTrigger 
                        onClick={() => toggleTable(table.table_name)}
                        className="flex items-center justify-between w-full p-3 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Table className="h-4 w-4 text-gray-500" />
                          <span className="font-medium">{table.table_name}</span>
                          <Badge variant="outline" className="text-xs">
                            {table.columns?.length || 0} columns
                          </Badge>
                        </div>
                        <RefreshCw className={`h-4 w-4 transition-transform ${
                          expandedTables[table.table_name] ? 'rotate-90' : ''
                        }`} />
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="ml-6 mt-2 border rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="text-left px-4 py-2 font-medium">Column</th>
                                <th className="text-left px-4 py-2 font-medium">Type</th>
                                <th className="text-left px-4 py-2 font-medium">Nullable</th>
                                <th className="text-left px-4 py-2 font-medium">Default</th>
                                <th className="text-left px-4 py-2 font-medium">Constraints</th>
                              </tr>
                            </thead>
                            <tbody>
                              {table.columns?.map((column, idx) => (
                                <tr key={column.column_name} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="px-4 py-2">
                                    <div className="flex items-center gap-2">
                                      {renderColumnIcon(column)}
                                      <span className="font-mono">{column.column_name}</span>
                                    </div>
                                  </td>
                                  <td className={`px-4 py-2 font-mono ${getDataTypeColor(column.data_type)}`}>
                                    {column.data_type}
                                  </td>
                                  <td className="px-4 py-2">
                                    <Badge variant={column.is_nullable ? 'secondary' : 'default'} className="text-xs">
                                      {column.is_nullable ? 'NULL' : 'NOT NULL'}
                                    </Badge>
                                  </td>
                                  <td className="px-4 py-2 font-mono text-xs">
                                    {column.column_default || '-'}
                                  </td>
                                  <td className="px-4 py-2">
                                    <div className="flex gap-1">
                                      {column.is_primary_key && (
                                        <Badge variant="default" className="text-xs">PK</Badge>
                                      )}
                                      {column.is_foreign_key && (
                                        <Badge variant="outline" className="text-xs">FK</Badge>
                                      )}
                                      {column.is_unique && (
                                        <Badge variant="outline" className="text-xs">UNIQUE</Badge>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="versions" className="space-y-4">
              {isLoadingVersions ? (
                <div className="flex justify-center p-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
                </div>
              ) : schemaVersions.length === 0 ? (
                <Alert>
                  <Clock className="h-4 w-4" />
                  <AlertTitle>No Version History</AlertTitle>
                  <AlertDescription>
                    Schema versioning will be available once the agent publishes schema updates.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-2">
                  {schemaVersions.map((version) => (
                    <div key={version.version_id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{version.version_id}</h4>
                          <p className="text-sm text-gray-600">
                            {new Date(version.created_at).toLocaleString()} • by {version.created_by}
                          </p>
                          {version.comment && (
                            <p className="text-sm text-gray-500 mt-1">{version.comment}</p>
                          )}
                        </div>
                        <Button
                          onClick={() => compareWithVersion(version)}
                          variant="outline"
                          size="sm"
                        >
                          Compare
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Schema Changes Dialog */}
      <Dialog open={showChangeDialog} onOpenChange={setShowChangeDialog}>
        <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Schema Changes</DialogTitle>
            <DialogDescription>
              Comparing {selectedVersion?.version_id} with current schema
            </DialogDescription>
          </DialogHeader>
          
          {schemaChanges && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium mb-2">Summary</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>Total changes: {schemaChanges.summary.total_changes}</div>
                  <div>Risk level: 
                    <Badge 
                      variant={schemaChanges.summary.risk_level === 'high' ? 'destructive' : 
                               schemaChanges.summary.risk_level === 'medium' ? 'default' : 'secondary'}
                      className="ml-2"
                    >
                      {schemaChanges.summary.risk_level}
                    </Badge>
                  </div>
                  {schemaChanges.summary.tables_added > 0 && (
                    <div className="text-green-600">Tables added: {schemaChanges.summary.tables_added}</div>
                  )}
                  {schemaChanges.summary.tables_removed > 0 && (
                    <div className="text-red-600">Tables removed: {schemaChanges.summary.tables_removed}</div>
                  )}
                  {schemaChanges.summary.columns_added > 0 && (
                    <div className="text-green-600">Columns added: {schemaChanges.summary.columns_added}</div>
                  )}
                  {schemaChanges.summary.columns_removed > 0 && (
                    <div className="text-red-600">Columns removed: {schemaChanges.summary.columns_removed}</div>
                  )}
                </div>
              </div>

              {/* Changes List */}
              <div className="space-y-2">
                <h4 className="font-medium">Changes</h4>
                {schemaChanges.changes.length === 0 ? (
                  <p className="text-sm text-gray-500">No schema changes detected</p>
                ) : (
                  schemaChanges.changes.map((change, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 rounded-lg hover:bg-gray-50">
                      {renderChangeIcon(change.type)}
                      <div className="flex-1">
                        <p className="text-sm">{change.details}</p>
                        {change.column_info && (
                          <p className="text-xs text-gray-500 mt-1">
                            Type: {change.column_info.data_type}, 
                            Nullable: {change.column_info.is_nullable ? 'Yes' : 'No'}
                          </p>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SchemaViewer;